/**
 * 商户端字典扩展层（P2-B 裸码修复 · C1.3）。
 *
 * 定位：web/shared/dictionaries 未覆盖、但商户端页面需要的少量域内字典。
 * 原则：优先复用 @shared 字典（CARD_STATUS / DEPOSIT_METHOD 等）；仅缺表时在本文件补，
 * 译名对齐端内既有映射（statusLabels.ts / api/positions.ts），禁止与 shared 双源漂移。
 */
import type { Dict } from '@shared/dictionaries/types';

/**
 * 通道充值单状态（头寸充值 position-topups，≠ 用户本金充值）。
 * 译名对齐 api/positions.ts TOPUP_STATUS_LABEL / statusLabels.ts POSITION_TOPUP_STATUS_LABEL；
 * 键对齐后端头寸充值状态：待付款 → 链上检测中 → 待人工复核 → 已到账（或异常/取消）。
 */
export const TOPUP_STATUS: Dict = {
  PENDING_PAYMENT: { label: '待付款', tone: 'warning' },
  DETECTING: { label: '链上检测中', tone: 'info' },
  PENDING_REVIEW: { label: '待人工复核', tone: 'warning' },
  CREDITED: { label: '已到账', tone: 'success' },
  EXCEPTION: { label: '异常', tone: 'danger' },
  CANCELLED: { label: '已取消', tone: 'neutral' },
};

/**
 * 通道充值申请渠道（充值指引付款通道）。
 * 译名对齐 statusLabels.ts POSITION_CHANNEL_LABEL（BANK 译「银行」，区别于入金 DEPOSIT_METHOD.BANK「电汇」）。
 */
export const TOPUP_CHANNEL: Dict = {
  CRYPTO: { label: '链上', tone: 'neutral' },
  BANK: { label: '银行', tone: 'neutral' },
};

/**
 * 邮件模板变更审计动作（EmailTemplateAuditLog ACTION_* 三值，后端真源）。
 */
export const EMAIL_TEMPLATE_AUDIT_ACTION: Dict = {
  CREATE: { label: '新建', tone: 'success' },
  UPDATE: { label: '更新', tone: 'info' },
  PUBLISH: { label: '发布', tone: 'info' },
};

/**
 * 安全日志审计动作（GET /settings/security-logs action 码）。
 * 后端 actionCode 为开放集合（LOGIN / CHANGE_PWD / MFA_BIND / KICK_DEVICE 等），
 * 此处仅收录已有据可查的码；未收录码经 getLabel 回退原始码展示（与三端「未知枚举回退原值」一致），
 * 故筛选下拉不再直出全部裸码。
 */
export const SECURITY_LOG_ACTION: Dict = {
  LOGIN: { label: '登录', tone: 'neutral' },
  LOGOUT: { label: '退出登录', tone: 'neutral' },
  CHANGE_PWD: { label: '修改密码', tone: 'neutral' },
  MFA_BIND: { label: '绑定 MFA', tone: 'neutral' },
  KICK_DEVICE: { label: '下线设备', tone: 'neutral' },
  QUALIFICATION_CHANGE_REQUEST: { label: '资质变更申请', tone: 'neutral' },
  RECEIVE_ACCOUNT_APPEAL: { label: '收款账户申诉', tone: 'neutral' },
};

// ================= 企业用户域（corporate 系） =================
// qa-report 遗留2：原散在 api/corporateCore.ts 的「码→中文」映射表统一升格为 Dict；
// 键 = 后端 allinks-domain .../model/corporate/ 各枚举 name() 全大写，
// 译名沿用端内既有映射（历史行为不变），tone 按语义色五类补齐。

/** 开户申请状态（OnboardingStatus，TINYINT 0~9；正向链 DRAFT→…→FILED + 分支）。 */
export const CORP_ONBOARDING_STATUS: Dict = {
  DRAFT: { label: '草稿', tone: 'neutral' },
  SUBMITTED: { label: '已提交', tone: 'info' },
  PENDING_APPROVAL: { label: '商户待审', tone: 'warning' },
  PENDING_RESUBMIT: { label: '待补件', tone: 'warning' },
  APPROVED: { label: '商户已通过', tone: 'success' },
  REJECTED: { label: '已驳回', tone: 'danger' },
  FILING: { label: '平台备案中', tone: 'info' },
  FILED: { label: '平台已备案', tone: 'success' },
  ESCALATED: { label: '转平台终审', tone: 'warning' },
  CANCELLED: { label: '已撤销', tone: 'neutral' },
};

/**
 * 平台备案侧状态：契约枚举 NOT_FILED/FILING/FILED/ESCALATED + 后端 ItemView 实况 NONE，
 * 两套口径都收敛进表（CorporateOnboardingController 实锚）。
 */
export const CORP_PLATFORM_STATUS: Dict = {
  NOT_FILED: { label: '未备案', tone: 'neutral' },
  NONE: { label: '未备案', tone: 'neutral' },
  FILING: { label: '备案中', tone: 'info' },
  FILED: { label: '已备案', tone: 'success' },
  ESCALATED: { label: '已转平台', tone: 'warning' },
};

/** 企业 KYB 档案结论（KybStatus，corporate_account.kyb_status TINYINT 0~3）。 */
export const CORP_KYB_STATUS: Dict = {
  NONE: { label: '未开户认证', tone: 'neutral' },
  VALID: { label: 'KYB 有效', tone: 'success' },
  INCOMPLETE: { label: '资料不全', tone: 'warning' },
  REJECTED: { label: '已驳回', tone: 'danger' },
};

/** 材料齐备状态（corporate-api §4.1 docStatus 契约口径 COMPLETE/INCOMPLETE，无独立枚举类）。 */
export const CORP_DOC_STATUS: Dict = {
  COMPLETE: { label: '齐件', tone: 'success' },
  INCOMPLETE: { label: '缺件', tone: 'warning' },
};

/** 审批单状态（CorpApprovalStatus，TINYINT 0~4）。 */
export const CORP_APPROVAL_STATUS: Dict = {
  PENDING_APPROVAL: { label: '待审批', tone: 'warning' },
  APPROVED: { label: '已通过', tone: 'success' },
  REJECTED: { label: '已拒绝', tone: 'danger' },
  EXPIRED: { label: '已超时', tone: 'neutral' },
  CANCELLED: { label: '已取消', tone: 'neutral' },
};

/** 预算类型（CorpBudgetType，corporate_budget.budget_type TINYINT 1~4）。 */
export const CORP_BUDGET_TYPE: Dict = {
  TOTAL: { label: '总预算', tone: 'neutral' },
  DEPARTMENT: { label: '部门预算', tone: 'neutral' },
  PROJECT: { label: '项目预算', tone: 'neutral' },
  CHANNEL_ACCOUNT: { label: '渠道账户预算', tone: 'neutral' },
};

/** 预算状态（CorpBudgetStatus，corporate_budget.status TINYINT 1~2）。 */
export const CORP_BUDGET_STATUS: Dict = {
  ACTIVE: { label: '生效中', tone: 'success' },
  PAUSED: { label: '已暂停', tone: 'warning' },
};

/** 报销单状态（CorpExpenseStatus，TINYINT 0~3）。 */
export const CORP_EXPENSE_STATUS: Dict = {
  SUBMITTED: { label: '已提交', tone: 'info' },
  REVIEWING: { label: '审核中', tone: 'warning' },
  REIMBURSED: { label: '已报销', tone: 'success' },
  REJECTED: { label: '已驳回', tone: 'danger' },
};

/** 风控规则类型（CorpRiskRuleType，TINYINT 1~4）。 */
export const CORP_RISK_RULE_TYPE: Dict = {
  MCC_WHITELIST: { label: 'MCC 白名单', tone: 'neutral' },
  LIMIT_SINGLE: { label: '单笔限额', tone: 'neutral' },
  LIMIT_DAILY: { label: '单日限额', tone: 'neutral' },
  LIMIT_MONTHLY: { label: '单月限额', tone: 'neutral' },
};

/** 风控规则作用域（CorpRiskRuleScope，TINYINT 1~4）。 */
export const CORP_RISK_RULE_SCOPE: Dict = {
  GLOBAL: { label: '全局', tone: 'neutral' },
  DEPARTMENT: { label: '部门', tone: 'neutral' },
  MEMBER: { label: '成员', tone: 'neutral' },
  CARD: { label: '卡片', tone: 'neutral' },
};

/** 风控规则启停状态（后端 TINYINT ENABLED=1/DISABLED=2 → 出参枚举名 ACTIVE/DISABLED）。 */
export const CORP_RISK_RULE_STATUS: Dict = {
  ACTIVE: { label: '启用', tone: 'success' },
  DISABLED: { label: '停用', tone: 'neutral' },
};

/** 账单聚合维度（CorpBillDimension，corporate_bill.dimension TINYINT 1~4）。 */
export const CORP_BILL_DIMENSION: Dict = {
  DEPARTMENT: { label: '按部门', tone: 'neutral' },
  MEMBER: { label: '按成员', tone: 'neutral' },
  PROJECT: { label: '按项目', tone: 'neutral' },
  CHANNEL_ACCOUNT: { label: '按渠道账户', tone: 'neutral' },
};

/** 账单状态（CorporateBill 聚合状态常量 STATUS_GENERATED=1 / STATUS_CONFIRMED=2，无独立枚举类）。 */
export const CORP_BILL_STATUS: Dict = {
  GENERATED: { label: '已生成', tone: 'info' },
  CONFIRMED: { label: '已确认', tone: 'success' },
};

/** 企业成员角色（CorpMemberRole，TINYINT 1~3）。 */
export const CORP_MEMBER_ROLE: Dict = {
  ADMIN: { label: '管理员', tone: 'info' },
  FINANCE: { label: '财务', tone: 'neutral' },
  EMPLOYEE: { label: '员工', tone: 'neutral' },
};

/** 企业成员状态（CorpMemberStatus，TINYINT 0~3）。 */
export const CORP_MEMBER_STATUS: Dict = {
  INVITED: { label: '邀请中', tone: 'warning' },
  ACTIVE: { label: '已激活', tone: 'success' },
  DISABLED: { label: '已禁用', tone: 'neutral' },
  OFFBOARDED: { label: '已离职', tone: 'neutral' },
};

/** 企业卡聚合状态（CorpCardStatus，TINYINT 1~3；CLOSED 为终态）。 */
export const CORP_CARD_STATUS: Dict = {
  ACTIVE: { label: '已激活', tone: 'success' },
  RECYCLED: { label: '已回收', tone: 'neutral' },
  CLOSED: { label: '已注销', tone: 'neutral' },
};

/**
 * 企业开卡维度（CARD-002 body.dimension；后端 CorporateCardController 以字符串口径
 * 判断 DEPARTMENT 批量 / 其余单卡，无独立枚举类，三值与页面口一致）。
 */
export const CORP_CARD_ISSUE_DIMENSION: Dict = {
  MEMBER: { label: '成员', tone: 'neutral' },
  DEPARTMENT: { label: '部门', tone: 'neutral' },
  PROJECT: { label: '项目', tone: 'neutral' },
};

/**
 * 商户门户子账号状态（settings/members；后端 MerchantMemberView.status 直出
 * PortalUser.status.name() = UserStatus 枚举名 ACTIVE/DISABLED/LOCKED，
 * 真源 allinks-domain .../model/auth/vo/UserStatus.java）。
 */
export const PORTAL_MEMBER_STATUS: Dict = {
  ACTIVE: { label: '启用', tone: 'success' },
  DISABLED: { label: '停用', tone: 'neutral' },
  LOCKED: { label: '已锁定', tone: 'danger' },
};
